/**
 * 防抖函数：延迟执行，高频触发时重置计时
 * @param fn 要执行的函数
 * @param delay 延迟时间（毫秒），默认300ms
 * @param immediate 是否立即执行（第一次触发时执行），默认false
 * @returns 防抖后的函数
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 300,
  immediate: boolean = false,
): T {
  let timer: NodeJS.Timeout | null = null

  // 显式指定 this 类型为原函数的 this 类型
  const debouncedFn = function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timer) clearTimeout(timer)

    const execute = () => {
      timer = null
      // 绑定正确的 this 上下文执行原函数
      fn.apply(this, args)
    }

    // 立即执行逻辑
    if (immediate && !timer) {
      fn.apply(this, args)
    } else {
      timer = setTimeout(execute, delay)
    }
  } as T

  return debouncedFn
}

/**
 * 节流函数：固定频率执行，高频触发时按间隔执行
 * @param fn 要执行的函数
 * @param interval 执行间隔（毫秒），默认300ms
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(fn: T, interval: number = 300): T {
  let lastTime = 0

  // 显式指定 this 类型为原函数的 this 类型
  const throttledFn = function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const now = Date.now()
    if (now - lastTime >= interval) {
      lastTime = now
      // 绑定正确的 this 上下文执行原函数
      fn.apply(this, args)
    }
  } as T

  return throttledFn
}

type ThrottleTrailingFn<T extends (...args: any[]) => any> = T & {
  cancel: () => void
}

/**
 * 带 trailing 的节流：首次立即执行，窗口内后续调用合并为窗口结束时再跑一次最后参数。
 * 用于松手提交这类「不能丢掉最后一次值」的场景。
 */
export function throttleTrailing<T extends (...args: any[]) => any>(
  fn: T,
  interval: number = 300,
): ThrottleTrailingFn<T> {
  let lastTime = 0
  let timer: NodeJS.Timeout | null = null
  /** 窗口内最后一次调用（已绑定 this / args），窗口结束时执行 */
  let lastCall: (() => void) | null = null

  const invoke = () => {
    lastTime = Date.now()
    timer = null
    const call = lastCall
    lastCall = null
    call?.()
  }

  const throttledFn = function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    lastCall = () => fn.apply(this, args)
    const remaining = interval - (Date.now() - lastTime)

    if (remaining <= 0 || remaining > interval) {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      invoke()
      return
    }

    if (!timer) {
      timer = setTimeout(invoke, remaining)
    }
  } as ThrottleTrailingFn<T>

  throttledFn.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    lastCall = null
  }

  return throttledFn
}
